import {
  Button,
  Checkbox,
  cn,
  RadioGroup,
  RadioGroupProps,
  RiFilter3Line,
  RiMap2Line,
  Stack,
  StackProps,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SpeciesGroupId, Threat } from '@/api/rest/resources/conservation';
import { FormControl, FormField, FormFieldProps, FormItem, FormLabel, withSuspenseBoundary } from '@/components';
import { FormRadioItem } from '@/components/widgets/o/Form/FormRadio';
import { Picker, PickerContent, PickerTrigger, usePicker } from '@/components/widgets/o/Form/Picker';
import { useIsRestorationProject } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/hooks/useIsRestorationProject';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useProject } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProject';
import { useProjectCells } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProjectPolygon';
import { useTilesetsByLayer } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useTilesets';
import { isNullish } from '@/utils/booleans';
import { range } from '@/utils/range';

import { useControlsContext } from '../hooks/useControlsForm';
import { ControlsFormFieldValues, FilterField } from '../types';

export const Controls = (props: StackProps) => {
  const controlsForm = useControlsContext();
  const [layer] = controlsForm.watch(['indicatorLayer']);

  return (
    <Stack direction='row' spacing={2} data-testid='interactive-map-controls' {...props}>
      <YearPicker />
      <LayerPicker />
      {layer === 'habitatIntactness' && <ThreatFilters />}
      {layer === 'floraFauna' && <SpeciesFilters />}
    </Stack>
  );
};

const YearPicker = () => {
  const { t } = useTranslation();

  const controlsForm = useControlsContext();
  const baselineYear = useFact<number>('r1_conservation_baseline_year').value;
  const currentYear = useFact<number>('r1_conservation_current_year').value;

  const years = range(baselineYear, currentYear)
    .reverse()
    .map((n) => n.toString());

  const selectedYear = controlsForm.watch('year');

  return (
    <Picker>
      <PickerTrigger data-testid='year-picker-trigger'>
        {selectedYear ?? t('shared.projects.project.conservation.interactiveMap.labels.year')}
      </PickerTrigger>
      <PickerContent data-testid='year-picker-content'>
        <FormRadioGroup name='year'>
          {years.map((item) => (
            <YearRadioButton key={item} year={item} />
          ))}
        </FormRadioGroup>
      </PickerContent>
    </Picker>
  );
};

const YearRadioButton = ({ year }: { year: string }) => {
  const projectCells = useProjectCells(undefined, year);
  const hasYearProjectCells = isNullish(projectCells) || projectCells?.features?.length === 0;

  return <FormRadioItem value={year} label={year} disabled={hasYearProjectCells} />;
};

const filterFields: FilterField[] = ['speciesFilter', 'threatFilters'];

const LayerPicker = () => {
  const { t } = useTranslation();
  const isRestorationProject = useIsRestorationProject();

  const controlsForm = useControlsContext();
  const layer = controlsForm.watch('indicatorLayer');

  const ecologicalIntegrityTimeSeriesValue = useFact<Record<string, number>>(
    'r1_conservation_ecological_integrity_ts',
  )?.value;

  const displayEcologicalIntegrityLayer = !isRestorationProject && !isNullish(ecologicalIntegrityTimeSeriesValue);
  return (
    <Picker>
      <PickerTrigger leftAdornment={<RiMap2Line />} data-testid='layer-picker-trigger'>
        {layer
          ? t(`shared.projects.project.conservation.interactiveMap.labels.${layer}`)
          : t('shared.projects.project.conservation.interactiveMap.labels.indicatorLayers')}
      </PickerTrigger>

      <PickerContent data-testid='layer-picker-content'>
        <FormRadioGroup
          name='indicatorLayer'
          onValueChange={() => {
            filterFields.forEach((field) => controlsForm.setValue(field, null));
          }}
        >
          {displayEcologicalIntegrityLayer && (
            <FormRadioItem
              value='ecologicalIntegrity'
              label={t('shared.projects.project.conservation.interactiveMap.labels.ecologicalIntegrity')}
            />
          )}
          <FormRadioItem
            value='habitatIntactness'
            label={t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness')}
          />
          {!isRestorationProject && (
            <FormRadioItem
              value='floraFauna'
              label={t('shared.projects.project.conservation.interactiveMap.labels.floraFauna')}
            />
          )}
        </FormRadioGroup>
      </PickerContent>
    </Picker>
  );
};

const ThreatFilters = withSuspenseBoundary(() => {
  const { t } = useTranslation();

  const controlsForm = useControlsContext();

  const [threatFilters] = controlsForm.watch(['threatFilters']);

  const [selected, setSelected] = useState<typeof threatFilters>([]);
  const [isOpen, setIsOpen] = useState(false);

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const resetForm = () => {
    setSelected(threatFilters);
  };

  const handleCancelClick = () => {
    resetForm();
    closeDropdown();
  };

  const handleSubmitClick = () => {
    controlsForm.setValue('threatFilters', selected);
    closeDropdown();
  };

  const tilesetsByLayer = useTilesetsByLayer() ?? {};

  const availableThreats = Object.entries(tilesetsByLayer)
    .filter(([tilesetId, value]) => {
      const isThreat = (maybeThreat: unknown): maybeThreat is Threat => {
        return Object.values(Threat).includes(maybeThreat as Threat);
      };

      if (!isThreat(tilesetId)) {
        return false;
      }

      if (!value) {
        return false;
      }

      // eslint-disable-next-line sonarjs/prefer-single-boolean-return
      if (!Object.keys(value).length) {
        return false;
      }

      return true;
    })
    .map(([tilesetId]) => tilesetId as Threat);

  return (
    <Picker
      open={isOpen}
      onOpenChange={(open) => {
        resetForm();
        setIsOpen(open);
      }}
    >
      <PickerTrigger leftAdornment={<RiFilter3Line />} data-testid='threat-picker-trigger'>
        {threatFilters?.length
          ? t('shared.projects.project.conservation.interactiveMap.labels.filtersApplied', {
              count: threatFilters.length,
            })
          : t('shared.projects.project.conservation.interactiveMap.labels.filterByThreats')}
      </PickerTrigger>

      <PickerContent data-testid='threat-picker-content'>
        <Stack spacing={4}>
          <Stack spacing={2}>
            {availableThreats.map((threat) => {
              return (
                <Checkbox
                  key={threat}
                  id={threat}
                  checked={selected?.includes(threat)}
                  onCheckedChange={() => {
                    const set = new Set(selected);
                    if (set.has(threat)) {
                      set.delete(threat);
                    } else {
                      set.add(threat);
                    }
                    setSelected(Array.from(set));
                  }}
                >
                  <FormLabel htmlFor={threat}>{t(`shared.projects.project.conservation.labels.${threat}`)}</FormLabel>
                </Checkbox>
              );
            })}
          </Stack>
          <Stack direction='row-reverse' spacing={2}>
            <Button variant='contained' size='small' onClick={handleSubmitClick}>
              {t('shared.projects.project.conservation.interactiveMap.labels.applyFilter')}
            </Button>
            <Button variant='text' size='small' onClick={handleCancelClick}>
              {t('global.ui.buttons.cancel')}
            </Button>
          </Stack>
        </Stack>
      </PickerContent>
    </Picker>
  );
}, null);

const SpeciesFilters = withSuspenseBoundary(() => {
  const { t } = useTranslation();

  const controlsForm = useControlsContext();

  const [speciesFilter] = controlsForm.watch(['speciesFilter']);

  const project = useProject();
  const speciesGroups = project?.speciesGroups ?? {};

  return (
    <Picker>
      <PickerTrigger leftAdornment={<RiFilter3Line />} data-testid='species-picker-trigger'>
        {speciesGroups[speciesFilter as SpeciesGroupId] ??
          t('shared.projects.project.conservation.interactiveMap.labels.filterBySpecies')}
      </PickerTrigger>

      <PickerContent data-testid='species-picker-content'>
        <Stack spacing={4}>
          <FormRadioGroup
            name='speciesFilter'
            onValueChange={() => {
              controlsForm.setValue('speciesFilter', null);
            }}
          >
            {(Object.keys(speciesGroups) as SpeciesGroupId[]).map((group) => (
              <FormRadioItem key={group} value={group} label={speciesGroups[group as SpeciesGroupId]} />
            ))}
          </FormRadioGroup>
          {speciesFilter && <ClearSpeciesFilterButton />}
        </Stack>
      </PickerContent>
    </Picker>
  );
}, null);

const ClearSpeciesFilterButton = () => {
  const { t } = useTranslation();

  const picker = usePicker();

  const controlsForm = useControlsContext();
  const [speciesFilter] = controlsForm.watch(['speciesFilter']);

  return (
    <Button
      variant='outline'
      size='small'
      disabled={!speciesFilter}
      onClick={() => {
        controlsForm.setValue('speciesFilter', null);
        picker.setOpen(false);
      }}
    >
      {t('shared.projects.project.conservation.interactiveMap.labels.clearFilter')}
    </Button>
  );
};

type FormRadioGroupProps = RadioGroupProps &
  Pick<FormFieldProps<Pick<ControlsFormFieldValues, 'year' | 'indicatorLayer' | 'speciesFilter'>>, 'name'>;

export const FormRadioGroup = ({ name, className, onValueChange, ...delegated }: FormRadioGroupProps) => {
  const controlsForm = useControlsContext();
  const { setOpen } = usePicker();

  return (
    <FormField
      name={name}
      control={controlsForm.control}
      render={({ field: { onChange, ...field } }) => (
        <FormItem>
          <FormControl>
            <RadioGroup
              className={cn('flex flex-col gap-2', className)}
              {...field}
              onValueChange={(...args) => {
                onValueChange?.(...args);
                onChange(...args);
                setOpen(false);
              }}
              data-testid={`radiogroup-${field.name}`}
              {...delegated}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};
